import { branding } from '@fsd/shared';
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  locale?: string;
  /** Product-specific schema data */
  product?: {
    name: string;
    price?: number;
    currency?: string;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
    condition?: 'NewCondition' | 'UsedCondition' | 'RefurbishedCondition';
    brand?: string;
    sku?: string;
    rating?: number;
    reviewCount?: number;
  };
  /** Breadcrumb navigation */
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
}

export function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  locale = 'pt_BR',
  product,
  breadcrumbs,
}: SEOProps) {
  const siteName = branding.brandName;
  const defaultTitle = `${siteName} - Drones Profissionais e Serviços de Pulverização Agrícola`;
  const defaultDescription =
    'Especialistas em drones profissionais e serviços de pulverização agrícola. Oferecemos tecnologia de ponta e soluções personalizadas para maximizar a produtividade do seu negócio.';
  const defaultKeywords =
    'drones, drones profissionais, pulverização agrícola, agricultura de precisão, drones para agricultura, drones novos, drones usados, tecnologia agrícola';
  const baseUrl = `https://${branding.primaryDomain}`;
  const defaultImage = `${baseUrl}/og-image.jpg`;
  const defaultUrl = baseUrl;

  const finalTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalImage = image || defaultImage;
  const finalUrl = url || defaultUrl;

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary Meta Tags
    updateMetaTag('title', finalTitle);
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);

    // Open Graph
    updateMetaTag('og:title', finalTitle, 'property');
    updateMetaTag('og:description', finalDescription, 'property');
    updateMetaTag('og:image', finalImage, 'property');
    updateMetaTag('og:url', finalUrl, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:site_name', siteName, 'property');
    updateMetaTag('og:locale', locale, 'property');

    // Twitter
    updateMetaTag('twitter:card', 'summary_large_image', 'property');
    updateMetaTag('twitter:title', finalTitle, 'property');
    updateMetaTag('twitter:description', finalDescription, 'property');
    updateMetaTag('twitter:image', finalImage, 'property');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', finalUrl);

    // Remove existing JSON-LD scripts for dynamic content
    document.querySelectorAll('script[data-seo-generated="true"]').forEach((el) => {
      el.remove();
    });

    // Add Product Schema if product data is provided
    if (product && type === 'product') {
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        image: finalImage,
        description: finalDescription,
        brand: {
          '@type': 'Brand',
          name: product.brand || branding.brandName,
        },
        ...(product.sku && { sku: product.sku }),
        ...(product.price && {
          offers: {
            '@type': 'Offer',
            url: finalUrl,
            priceCurrency: product.currency || 'BRL',
            price: product.price,
            availability: `https://schema.org/${product.availability || 'InStock'}`,
            itemCondition: `https://schema.org/${product.condition || 'NewCondition'}`,
          },
        }),
        ...(product.rating &&
          product.reviewCount && {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: product.rating,
              reviewCount: product.reviewCount,
            },
          }),
      };

      const productScript = document.createElement('script');
      productScript.type = 'application/ld+json';
      productScript.setAttribute('data-seo-generated', 'true');
      productScript.textContent = JSON.stringify(productSchema);
      document.head.appendChild(productScript);
    }

    // Add BreadcrumbList Schema if breadcrumbs are provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: `${baseUrl}${crumb.url}`,
        })),
      };

      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.setAttribute('data-seo-generated', 'true');
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }
  }, [
    finalTitle,
    finalDescription,
    finalKeywords,
    finalImage,
    finalUrl,
    type,
    locale,
    siteName,
    product,
    breadcrumbs,
    baseUrl,
  ]);

  return null;
}
